import { memo, useMemo, useCallback, type SyntheticEvent } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { type Grader } from '../api';
import { renderGraderOption, getGraderLabel, filterGraderOptions } from './GraderOptionRenderer';
import { AUTOCOMPLETE_SX } from './filterStyles';
import { toArray } from './filterUtils';

interface GraderMultiSelectProps {
    /** All available graders */
    graders: Grader[];
    /** Selected grader IDs */
    value: string[] | undefined;
    /** Callback when selection changes - receives array of grader IDs or undefined */
    onChange: (graderIds: string[] | undefined) => void;
    /** Label for the autocomplete */
    label?: string;
    /** Component size */
    size?: 'small' | 'medium';
    /** Min width of the component */
    minWidth?: number;
    /** Max width of the component */
    maxWidth?: number;
}

function GraderMultiSelectComponent({
    graders,
    value,
    onChange,
    label = 'Grader',
    size = 'small',
    minWidth = 220,
    maxWidth = 350,
}: GraderMultiSelectProps) {
    // Sort graders alphabetically by name
    const sortedGraders = useMemo(() => [...graders].sort((a, b) => a.name.localeCompare(b.name)), [graders]);

    // Convert value (single or array) to selected Grader objects
    const selectedGraders = useMemo(() => {
        const graderIds = toArray(value);
        return graders.filter(g => graderIds.includes(g.id));
    }, [graders, value]);

    const handleChange = useCallback(
        (_event: SyntheticEvent, values: Grader[]) => {
            const graderIds = values.map(v => v.id);
            onChange(graderIds.length > 0 ? graderIds : undefined);
        },
        [onChange],
    );

    return (
        <Autocomplete<Grader, true>
            multiple
            size={size}
            sx={{ minWidth, maxWidth, ...AUTOCOMPLETE_SX }}
            options={sortedGraders}
            getOptionLabel={getGraderLabel}
            filterOptions={filterGraderOptions}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={selectedGraders}
            onChange={handleChange}
            renderOption={renderGraderOption}
            renderInput={params => <TextField {...params} label={label} />}
            renderValue={(values, getItemProps) =>
                values.map((grader, index) => {
                    const { key, ...tagProps } = getItemProps({ index });
                    return <Chip key={key} label={grader.name} size="small" {...tagProps} />;
                })
            }
        />
    );
}

export const GraderMultiSelect = memo(GraderMultiSelectComponent);
